import React, { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import DOMPurify from 'dompurify';

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  align?: 'left' | 'center' | 'right';
  onAlignChange?: (align: 'left' | 'center' | 'right') => void;
};

const ALLOWED_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'strong',
  'em',
  'u',
  'mark',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'a',
  'br',
  'span',
  'div',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'style'];

const sanitize = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['style'],
    ALLOWED_URI_REGEXP: /^https?:/i,
  });

export const RichTextEditor: React.FC<Props> = ({ value, onChange, placeholder, align = 'left', onAlignChange }) => {
  const safeInitial = useMemo(() => sanitize(value || '<p></p>'), [value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
        codeBlock: false,
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      Link.configure({
        protocols: ['http', 'https'],
        openOnClick: true,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Type rich text...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'listItem'],
        alignments: ['left', 'center', 'right'],
      }),
    ],
    content: safeInitial,
    editorProps: {
      handleDOMEvents: {
        keydown: (_view, event: any) => {
          if (event.key === 'Tab' && editor) {
            if (event.shiftKey) {
              editor.chain().focus().liftListItem('listItem').run();
            } else {
              editor.chain().focus().sinkListItem('listItem').run();
            }
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
    },
    onUpdate({ editor }) {
      const html = sanitize(editor.getHTML());
      onChange(html);
    },
    onCreate({ editor }) {
      const html = sanitize(editor.getHTML());
      if (html !== value) {
        onChange(html);
      }
      if (align && align !== 'left') {
        editor.chain().setTextAlign(align).run();
      }
    },
  });

  useEffect(() => {
    if (editor && align) {
      editor.commands.setTextAlign(align);
    }
  }, [editor, align]);

  const run = (fn: () => void) => {
    if (!editor) return;
    editor.chain().focus();
    fn();
  };

  const toggleLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Link URL (https://...)', prev);
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    if (!/^https?:\/\//i.test(url)) return;
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' })
      .run();
  };

  const alignAndStore = (val: 'left' | 'center' | 'right') => {
    if (!editor) return;
    editor.chain().focus().setTextAlign(val).run();
    onAlignChange?.(val);
  };

  const active = (name: string, attrs: Record<string, any> = {}) => editor?.isActive(name, attrs);
  const alignActive = (dir: 'left' | 'center' | 'right') => editor?.isActive({ textAlign: dir });
  const linkActive = () => editor?.isActive('link');

  const Button = ({
    activeState,
    onClick,
    children,
    title,
  }: {
    activeState?: boolean;
    onClick: () => void;
    children: React.ReactNode;
    title?: string;
  }) => (
    <button
      type="button"
      title={title}
      className={`px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-xs ${
        activeState
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100'
      }`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );

  return (
    <div className="space-y-2">
      <style>
        {`
          .rt-scope ul { list-style: disc; padding-left: 1.2rem; margin: 0.35rem 0; }
          .rt-scope ol { list-style: decimal; padding-left: 1.2rem; margin: 0.35rem 0; }
          .rt-scope li { margin: 0.15rem 0; }
          .rt-scope h1 { font-size: 1.35rem; font-weight: 700; margin: 0.4rem 0; }
          .rt-scope h2 { font-size: 1.2rem; font-weight: 700; margin: 0.35rem 0; }
          .rt-scope h3 { font-size: 1.05rem; font-weight: 700; margin: 0.3rem 0; }
          .rt-scope h4 { font-size: 0.95rem; font-weight: 700; margin: 0.25rem 0; }
          .rt-scope p { margin: 0.3rem 0; }
          .rt-scope p:empty::before { content: '\\00a0'; display: block; min-height: 1rem; }
          .rt-scope blockquote { border-left: 3px solid rgba(59,130,246,0.6); padding-left: 0.75rem; margin: 0.4rem 0; color: inherit; }
          .rt-scope code { background: rgba(148,163,184,0.15); padding: 0.1rem 0.25rem; border-radius: 4px; }
        `}
      </style>
      <div className="flex flex-wrap gap-2 text-xs sticky top-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur rounded-lg p-2 border border-gray-200 dark:border-gray-800">
        <Button activeState={!!active('bold')} onClick={() => run(() => editor?.chain().toggleBold().run())}>
          Bold
        </Button>
        <Button activeState={!!active('italic')} onClick={() => run(() => editor?.chain().toggleItalic().run())}>
          Italic
        </Button>
        <Button activeState={!!active('underline')} onClick={() => run(() => editor?.chain().toggleUnderline().run())}>
          Underline
        </Button>
        <Button activeState={!!active('highlight')} onClick={() => run(() => editor?.chain().toggleHighlight().run())}>
          Highlight
        </Button>
        <Button activeState={!!active('bulletList')} onClick={() => run(() => editor?.chain().toggleBulletList().run())}>
          Bullets
        </Button>
        <Button activeState={!!active('orderedList')} onClick={() => run(() => editor?.chain().toggleOrderedList().run())}>
          Numbered
        </Button>
        <Button onClick={() => run(() => editor?.chain().sinkListItem('listItem').run())}>Indent</Button>
        <Button onClick={() => run(() => editor?.chain().liftListItem('listItem').run())}>Outdent</Button>
        <Button activeState={!!active('blockquote')} onClick={() => run(() => editor?.chain().toggleBlockquote().run())}>
          Quote
        </Button>
        <Button activeState={!!active('code')} onClick={() => run(() => editor?.chain().toggleCode().run())}>
          Code
        </Button>
        <Button activeState={!!active('heading', { level: 1 })} onClick={() => run(() => editor?.chain().toggleHeading({ level: 1 }).run())}>
          H1
        </Button>
        <Button activeState={!!active('heading', { level: 2 })} onClick={() => run(() => editor?.chain().toggleHeading({ level: 2 }).run())}>
          H2
        </Button>
        <Button activeState={!!active('heading', { level: 3 })} onClick={() => run(() => editor?.chain().toggleHeading({ level: 3 }).run())}>
          H3
        </Button>
        <Button activeState={!!active('heading', { level: 4 })} onClick={() => run(() => editor?.chain().toggleHeading({ level: 4 }).run())}>
          H4
        </Button>
        <Button activeState={!!active('paragraph')} onClick={() => run(() => editor?.chain().setParagraph().run())}>
          Paragraph
        </Button>
        <Button activeState={!!linkActive()} onClick={toggleLink}>
          Link
        </Button>
        <Button
          activeState={!!linkActive()}
          onClick={() => run(() => editor?.chain().extendMarkRange('link').unsetLink().run())}
        >
          Clear link
        </Button>
        <Button activeState={!!alignActive('left')} onClick={() => alignAndStore('left')}>
          Left
        </Button>
        <Button activeState={!!alignActive('center')} onClick={() => alignAndStore('center')}>
          Center
        </Button>
        <Button activeState={!!alignActive('right')} onClick={() => alignAndStore('right')}>
          Right
        </Button>
        <Button onClick={() => run(() => editor?.chain().undo().run())}>Undo</Button>
        <Button onClick={() => run(() => editor?.chain().redo().run())}>Redo</Button>
      </div>

      <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
        {editor ? (
          <EditorContent
            editor={editor}
            className="min-h-[140px] focus:outline-none rt-scope"
            style={{ textAlign: align }}
          />
        ) : (
          <div className="text-gray-500 text-xs">Loading editor…</div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
